import * as T from 'three';
import { AnimatedCharacter, type Pose } from '@london-life/game/characters';
import type { CrowdMember } from '@london-life/contracts';
import type { Character } from '@london-life/game/game';
import { bodyHit } from './tap';
import { findPath, nearestMain, randomOpen, type WalkGrid } from './venues';

// The people in a venue: each one walks a path round the props, then stands a while. Others wander on their
// own; your character goes where you send it (the spot of what you are doing, or where the floor was tapped)
// and stays there. The place's own people (staff and visitors from the content) hold their pose where they
// are put (behind the bar, on a bench), and walkers go round their route.

type Walker = {
  actor: AnimatedCharacter;
  path: { x: number; z: number }[];
  // seconds left standing before wandering on (others only)
  rest: number;
  wanders: boolean;
  ready: boolean;
  // what to do on arrival
  then?: () => void;
  // someone who is always there (staff, a visitor): their pose, and the round they walk
  member?: CrowdMember;
  round?: number;
};
const SPEED = 1.1;
// the characters are drawn 2.4 high for the map; in a room they stand a little taller than life next to the
// furniture, as in Lagos Life, so people read clearly on a phone
const SCALE = 0.82;
// how tall someone stands in a room (for a tap on them)
const HEIGHT = 2.4 * SCALE + 0.1;

export class VenueCrowd {
  readonly group = new T.Group();
  private walkers = new Map<string, Walker>();
  private head = new T.Vector3();
  private feet = new T.Vector3();
  constructor(
    private grid: WalkGrid,
    private random = Math.random,
  ) {}

  // the room changed (a home's furniture moved): walk round the new layout
  setGrid(grid: WalkGrid) {
    this.grid = grid;
  }
  // where someone stands now
  at(id: string) {
    const p = this.walkers.get(id)?.actor.group.position;
    return p ? { x: p.x, z: p.z } : null;
  }

  has(id: string) {
    return this.walkers.has(id);
  }
  ids() {
    return [...this.walkers.keys()];
  }

  add(id: string, character: Character, at: { x: number; z: number }, wanders: boolean) {
    if (this.walkers.has(id)) return;
    const actor = new AnimatedCharacter(character);
    const start = nearestMain(this.grid, at.x, at.z);
    const walker: Walker = { actor, path: [], rest: 1 + this.random() * 4, wanders, ready: false };
    this.walkers.set(id, walker);
    // a character is measured at the origin while it loads (its hair is fitted then), so it is only moved,
    // turned and sized once it is ready
    actor.group.visible = false;
    this.group.add(actor.group);
    void actor.ready
      .then(() => {
        const position = actor.group.position;
        // sent somewhere while loading: start where it was meant to start
        if (!position.lengthSq()) position.set(start.x, 0, start.z);
        actor.group.rotation.y = this.random() * Math.PI * 2;
        actor.group.scale.setScalar(SCALE);
        actor.group.visible = true;
        walker.ready = true;
      })
      .catch(() => {});
  }

  // one of the place's own people
  addMember(id: string, character: Character, member: CrowdMember) {
    if (this.walkers.has(id)) return;
    const actor = new AnimatedCharacter(character);
    const walker: Walker = { actor, path: [], rest: 0, wanders: false, ready: false, member, round: 0 };
    this.walkers.set(id, walker);
    actor.group.visible = false;
    this.group.add(actor.group);
    void actor.ready
      .then(() => {
        actor.group.position.set(member.x, member.y ?? 0, member.z);
        actor.group.rotation.y = (member.face * Math.PI) / 180;
        actor.group.scale.setScalar(SCALE);
        actor.setPose(member.pose as Pose, member.seat ?? 0.45);
        actor.group.visible = true;
        walker.ready = true;
      })
      .catch(() => {});
  }

  // the place's own person under a ray (a tap on them, anywhere in a body-sized box), and what they do there
  memberAt(ray: T.Raycaster): { id: string; role: string } | null {
    let best: { id: string; role: string; distance: number } | null = null;
    for (const [id, walker] of this.walkers) {
      if (!walker.member || !walker.ready) continue;
      const distance = bodyHit(ray.ray, walker.actor.group.getWorldPosition(this.feet), HEIGHT);
      if (distance !== null && (!best || distance < best.distance)) best = { id, role: walker.member.role, distance };
    }
    return best ? { id: best.id, role: best.role } : null;
  }

  remove(id: string) {
    const walker = this.walkers.get(id);
    if (!walker) return;
    this.group.remove(walker.actor.group);
    walker.actor.dispose();
    this.walkers.delete(id);
  }

  // send someone somewhere; `then` runs when they get there (straight away if there is no way)
  walkTo(id: string, to: { x: number; z: number }, then?: () => void) {
    const walker = this.walkers.get(id);
    if (!walker) return;
    if (!walker.ready) {
      // not loaded yet: go once it is
      void walker.actor.ready.then(() => this.walkTo(id, to, then)).catch(() => {});
      return;
    }
    const from = walker.actor.group.position;
    walker.path = findPath(this.grid, { x: from.x, z: from.z }, to);
    walker.then = then;
    if (!walker.path.length) {
      walker.then = undefined;
      then?.();
    }
  }

  // a one-off move: wave, or do the thing at a spot
  act(id: string, motion: 'wave' | 'interact') {
    this.walkers.get(id)?.actor.setMotion(motion);
  }

  // hold a pose where they stand (dancing on a dance floor), or let it go
  pose(id: string, pose: Pose | null) {
    const walker = this.walkers.get(id);
    if (walker?.ready) walker.actor.setPose(pose ?? 'stand');
  }

  // turn to face a point
  face(id: string, at: { x: number; z: number }) {
    const walker = this.walkers.get(id);
    if (!walker) return;
    const p = walker.actor.group.position;
    walker.actor.group.rotation.y = Math.atan2(at.x - p.x, at.z - p.z);
  }

  // where someone's head is on the screen (for their name tag); null when not drawn yet
  headOnScreen(id: string, camera: T.Camera, width: number, height: number) {
    const walker = this.walkers.get(id);
    if (!walker?.ready) return null;
    this.head.copy(walker.actor.group.position);
    this.head.y = 2.4 * walker.actor.group.scale.y + 0.12;
    this.head.project(camera);
    if (this.head.z > 1) return null;
    return { x: ((this.head.x + 1) / 2) * width, y: ((1 - this.head.y) / 2) * height };
  }

  update(dt: number) {
    for (const walker of this.walkers.values()) {
      const { actor } = walker;
      if (!walker.ready) continue;
      const next = walker.path[0];
      if (next) {
        const p = actor.group.position;
        const dx = next.x - p.x,
          dz = next.z - p.z;
        const distance = Math.hypot(dx, dz);
        const step = SPEED * dt;
        if (distance <= step) {
          p.x = next.x;
          p.z = next.z;
          walker.path.shift();
          if (!walker.path.length) {
            if (!walker.member) actor.setMotion('idle');
            walker.rest = 2 + this.random() * 6;
            const then = walker.then;
            walker.then = undefined;
            then?.();
          }
        } else {
          p.x += (dx / distance) * step;
          p.z += (dz / distance) * step;
          actor.group.rotation.y = Math.atan2(dx, dz);
          actor.setMotion('walk');
        }
      } else if (walker.member?.path) {
        // a walker goes on to the next point of their round
        const route = walker.member.path;
        walker.round = ((walker.round ?? 0) + 1) % route.length;
        walker.path = [route[walker.round]!];
      } else if (walker.wanders) {
        walker.rest -= dt;
        if (walker.rest <= 0) {
          walker.rest = 3 + this.random() * 6;
          const p = actor.group.position;
          walker.path = findPath(this.grid, { x: p.x, z: p.z }, randomOpen(this.grid, this.random));
        }
      }
      actor.update(dt);
    }
  }

  dispose() {
    for (const id of this.ids()) this.remove(id);
  }
}
